import { CalendarViewModel } from '@views';
import { addMinutes, getHours, setHours, subMinutes, endOfDay } from 'date-fns';
import {
  Formats, IMeeting, IMeetingDto, IMeetingToDto, IScheduleCalendarMeeting,
  IScheduleCalendarMeetingResponse, RepeatTypes,
} from '../types';
import { MembersStore } from '../../store';
import { useStore, useViewModel } from '../hooks';
import { getRepeatType } from './meetingSetup';
import { IntervalFormats } from '../constants';
import { roundUpTime } from '../helpers';
import { RoomsColors } from '../constants/RoomsColors';

const calendarViewModel = useViewModel(CalendarViewModel);

const getDateInUtc = (stringDate: string) => {
  const date = new Date(stringDate);
  date.setSeconds(0);
  date.setMilliseconds(0);
  const offset = -date.getTimezoneOffset();
  return subMinutes(date, offset);
}

export const getDateInUserTimezone = (stringDate: string, offset: number) => {
  const date = getDateInUtc(stringDate);
  return addMinutes(date, offset);
}

export const transformDateToUtcString = (date: Date, offset: number) => {
  const systemOffset = -date.getTimezoneOffset();
  const offsetWithoutSystem = offset - systemOffset;
  const transformDate = subMinutes(date, offsetWithoutSystem);

  return transformDate.toISOString();
}

export const getMeetingFromDto = ({
  meetingId, meetingName, description, startMeetingDate, endMeetingDate, conferenceRoom, 
  meetingType, link, members, initiator, calendarColor, calendarId, calendarName, intervalType,
  meetingSetup, meetingConfirmation, outerMembers, meetingConfirmationForOuterUsers,
}: IMeetingDto, offset: number, isAllRooms = false): IMeeting => {
  const membersStore = useStore(MembersStore);
  const startDateFromDto = getDateInUserTimezone(startMeetingDate, offset);
  const endDateFromDto = getDateInUserTimezone(endMeetingDate, offset)
  return {
    id: meetingId,
    meetingName,
    description,
    startDate: startDateFromDto,
    endDate: endDateFromDto,
    oldStartDate: startDateFromDto,
    oldEndDate: endDateFromDto,
    format: meetingType,
    [meetingType === Formats.OFFLINE ? 'room' : 'link']:
      meetingType === Formats.OFFLINE
        ? conferenceRoom
        : link,
    members: members?.map((member) => membersStore.getMemberById(member))?.filter((elem) => elem),
    initiator: membersStore.membersWithCurrentUser.find((member) => member.id === initiator),
    calendarColor: isAllRooms ? RoomsColors[conferenceRoom.conferenceType] : calendarColor,
    calendarId,
    calendarName,
    intervalType,
    meetingSetup: {
      numberOfRepeat: meetingSetup?.numberOfRepeat || 1,
      expDate: meetingSetup?.expDate ? new Date(meetingSetup.expDate) : getDateInUserTimezone(endMeetingDate, offset),
      daysOfWeek: meetingSetup?.daysOfWeek || [],
      intervalFormat: meetingSetup?.intervalFormat || IntervalFormats[1].value,
      interval: meetingSetup?.interval || 1,
    },
    meetingConfirmation,
    outerMemberConfirmation: meetingConfirmationForOuterUsers,
    outerMembers,
    repeatType: getRepeatType(meetingSetup),
    modifyRecurringMeetingType: 'ONLY_THIS_MEETING',
    layer: 0,
  };
};

export const createMeeting = (dataBase?: Partial<IMeeting>): IMeeting => {
  const date = calendarViewModel.today
  const startDate = roundUpTime(date);
  const endDate = roundUpTime(setHours(date, getHours(date) + 1));
  return {
    id: dataBase?.id ?? null,
    meetingName: dataBase?.meetingName ?? '',
    description: dataBase?.description ?? '',
    startDate: dataBase?.startDate ?? startDate,
    endDate: dataBase?.endDate ?? endDate,
    oldStartDate: dataBase?.oldStartDate ?? startDate,
    oldEndDate: dataBase?.oldEndDate ?? endDate,
    format: dataBase?.format ?? Formats.ONLINE,
    room: dataBase?.room ?? undefined,
    link: dataBase?.link ?? '',
    members: dataBase?.members ?? [],
    initiator: dataBase?.initiator,
    calendarColor: '#4285F4',
    calendarId: dataBase?.calendarId ?? -1,
    calendarName: dataBase?.calendarName ?? 'РЕЛЭКС.Календарь',
    intervalType: dataBase?.intervalType ?? 'WITHOUT_REPEAT',
    repeatType: dataBase?.repeatType ?? RepeatTypes.DATE,
    meetingSetup: dataBase?.meetingSetup ?? {
      interval: 1,
      intervalFormat: 'WEEK',
      daysOfWeek: [],
      expDate: endOfDay(dataBase?.endDate ?? new Date()),
      numberOfRepeat: 20,
    },
    modifyRecurringMeetingType: 'ONLY_THIS_MEETING',
    outerMembers: dataBase?.outerMembers ?? [],
    meetingConfirmation: dataBase?.meetingConfirmation,
    outerMemberConfirmation: dataBase?.outerMemberConfirmation ?? [],
    layer: 0,
  }
};

export const getMeetingToDto = ({
  id, meetingName, description, startDate, endDate, oldStartDate, oldEndDate, format, room,
  link, members, intervalType, meetingSetup, repeatType, modifyRecurringMeetingType, outerMembers
}: IMeeting, offset: number): IMeetingToDto => ({
  meetingId: id,
  meetingName,
  description,
  startMeetingDate: transformDateToUtcString(startDate, offset),
  endMeetingDate: transformDateToUtcString(endDate, offset),
  oldStartMeetingDate: transformDateToUtcString(oldStartDate, offset),
  oldEndMeetingDate: transformDateToUtcString(oldEndDate, offset),
  meetingType: format,
  conferenceRoom: format === Formats.OFFLINE && room?.conferenceType ? room?.conferenceType : null,
  link: format === Formats.ONLINE && link ? link : null,
  participantIdsList: members.map((member) => member.id),
  intervalType,
  meetingSetup: intervalType === 'WITHOUT_REPEAT' ? null : {
    interval: intervalType === 'ANOTHER' ? meetingSetup.interval : null,
    intervalFormat: intervalType === 'ANOTHER' ? meetingSetup.intervalFormat : null,
    daysOfWeek: intervalType === 'ANOTHER' && meetingSetup.intervalFormat === 'WEEK' ? meetingSetup.daysOfWeek : [],
    expDate: repeatType === RepeatTypes.DATE ? meetingSetup.expDate : null,
    numberOfRepeat: repeatType === RepeatTypes.NUMBER ? meetingSetup.numberOfRepeat : null,
  },
  outerMembers: outerMembers.map(elem => ({name: elem.name, email: elem.email})),
  modifyRecurringMeetingType: !id || intervalType === 'WITHOUT_REPEAT' ? null : modifyRecurringMeetingType,
});

export const getMinifiedMeetingFromDto = (
  minifiedMeeting: IScheduleCalendarMeetingResponse, offset: number
): IScheduleCalendarMeeting => ({
  id: minifiedMeeting.meetingId,
  color: minifiedMeeting.calendarColorHexCode,
  name: minifiedMeeting.meetingName,
  startTime: getDateInUserTimezone(minifiedMeeting.meetingStart, offset),
  endTime: getDateInUserTimezone(minifiedMeeting.meetingEnd, offset),
  intervalType: minifiedMeeting.meetingIntervalType,
  calendarId: minifiedMeeting.calendarId,
})
